// Sections.jsx — warm editorial home content
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Display, Eyebrow, Reveal, Btn, Icon, Mark, Wordmark, Loader } from "./Primitives";
import { ProductCard } from "./Shop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WRAP = { maxWidth: "var(--maxw)", margin: "0 auto", padding: "0 clamp(20px,4vw,56px)" };

export function Intro() {
  return (
    <section id="intro" style={{ ...WRAP, padding: "clamp(80px,13vh,150px) clamp(20px,4vw,56px)" }}>
      <div className="k-intro-split" style={{ display: "flex", justifyContent: "space-between", gap: "clamp(28px,6vw,90px)", alignItems: "flex-end" }}>
        <Reveal style={{ flex: "1 1 0", maxWidth: 820 }}>
          <Eyebrow style={{ display: "block", marginBottom: 26 }}>Why Klaren</Eyebrow>
          <Display size="clamp(30px,4.4vw,60px)" style={{ fontWeight: 400, lineHeight: 1.12 }}>
            The things you touch every day <span className="k-serif-i" style={{ color: "var(--ink-2)" }}>deserve better.</span>
          </Display>
        </Reveal>
        <Reveal delay={0.12} style={{ flex: "0 1 380px" }}>
          <p style={{ color: "var(--ink-2)", fontSize: 16.5, lineHeight: 1.7 }}>
            Your hands. Your clothes. Your dishes. Your home. Klaren is everyday care that feels less harsh, less loud, and far more considered — made the way the best skincare is made.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function Range({ products, loading, onOpen, onAdd }) {
  return (
    <section id="range" style={{ background: "linear-gradient(180deg, var(--paper), var(--sand) 120%)", padding: "clamp(70px,11vh,130px) 0" }}>
      <div style={{ ...WRAP }}>
        <Reveal style={{ textAlign: "center", maxWidth: 760, margin: "0 auto" }}>
          <Eyebrow>Three daily essentials</Eyebrow>
          <Display size="clamp(34px,5vw,68px)" style={{ marginTop: 16, fontWeight: 400 }}>Made gentler, on purpose.</Display>
          <p style={{ color: "var(--ink-2)", fontSize: 16.5, lineHeight: 1.65, marginTop: 18 }}>One for the kitchen, one for the basin, one for the wash. Each made for the things that touch your skin, your home, and the people in it.</p>
        </Reveal>
        {loading ? <Loader label="Gathering the range" /> : (
          <div className="k-range-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(18px,2.2vw,30px)", marginTop: "clamp(44px,7vh,72px)" }}>
            {products.map((p, i) => <Reveal key={p.id} delay={0.07 * i}><ProductCard p={p} onOpen={onOpen} onAdd={onAdd} /></Reveal>)}
          </div>
        )}
      </div>
    </section>
  );
}

export function Philosophy() {
  const values = [
    ["leaf", "No added colour", "We leave out unnecessary dyes — because clean doesn’t need to be coloured to feel real."],
    ["hand-heart", "Skin-kind thinking", "Made for the things that touch your hands, your clothes, your dishes, your home."],
    ["drop-half", "Clean-rinse feel", "Designed to clean effectively, then rinse away — without the harsh after-feel."],
    ["flask", "Thoughtful formulas", "Plant-derived cleansers, gentle daily-use design, and honest ingredient choices."],
  ];
  return (
    <section id="philosophy" style={{ padding: "clamp(80px,13vh,150px) 0", background: "var(--paper)" }}>
      <div style={{ ...WRAP, textAlign: "center" }}>
        <Reveal><Eyebrow>Our philosophy</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <Display size="clamp(34px,5.4vw,76px)" style={{ margin: "18px auto 0", maxWidth: 900, fontWeight: 400 }}>Not just clean. <span className="k-serif-i" style={{ color: "var(--ink-2)" }}>Considered clean.</span></Display>
        </Reveal>
        <div className="k-values-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "clamp(16px,1.8vw,24px)", marginTop: "clamp(48px,8vh,84px)", textAlign: "left" }}>
          {values.map(([ic, t, d], i) => (
            <Reveal key={t} delay={0.07 * i}>
              <div style={{ background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 10, padding: "30px 26px", height: "100%" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--sand)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 22 }}><Icon name={ic} size={22} color="var(--ink)" /></div>
                <Display size={23} style={{ fontWeight: 500 }}>{t}</Display>
                <p style={{ color: "var(--ink-2)", fontSize: 14.5, lineHeight: 1.6, marginTop: 11 }}>{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeaveOutColumn({ items, kind }) {
  return (
    <div style={{ flex: "1 1 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
        <Icon name={kind === "choose" ? "check-circle" : "x-circle"} size={22} color={kind === "choose" ? "var(--sage)" : "var(--ink-3)"} weight={kind === "choose" ? "fill" : "light"} />
        <span className="k-eye">{kind === "choose" ? "We choose" : "We avoid"}</span>
      </div>
      {items.map((it) => (
        <div key={it} style={{ display: "flex", alignItems: "center", gap: 12, padding: "15px 0", borderTop: "1px solid var(--line)", color: kind === "choose" ? "var(--ink)" : "var(--ink-3)" }}>
          <span style={{ fontSize: 16.5, fontFamily: "var(--font-display)", textDecoration: kind === "avoid" ? "line-through" : "none", textDecorationColor: "var(--line)" }}>{it}</span>
        </div>
      ))}
    </div>
  );
}

export function LeaveOut() {
  const choose = ["Plant-derived cleansers", "Clean-rinse formulas", "No added colour", "Gentle daily-use design", "Thoughtful fragrance choices"];
  const avoid = ["Added food colour", "Optical brighteners", "Chlorine bleach", "Heavy perfume", "Fear-based germ claims"];
  return (
    <section id="promise" style={{ padding: "clamp(70px,11vh,130px) 0", background: "linear-gradient(180deg, var(--sand), var(--paper))" }}>
      <div style={{ ...WRAP }}>
        <Reveal style={{ maxWidth: 720 }}>
          <Eyebrow style={{ display: "block", marginBottom: 22 }}>Our promise</Eyebrow>
          <Display size="clamp(30px,4.6vw,62px)" style={{ fontWeight: 400, lineHeight: 1.1 }}>What we leave out <span className="k-serif-i" style={{ color: "var(--ink-2)" }}>matters too.</span></Display>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="k-leaveout" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(28px,5vw,80px)", marginTop: "clamp(40px,6vh,64px)" }}>
            <LeaveOutColumn items={choose} kind="choose" />
            <LeaveOutColumn items={avoid} kind="avoid" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer({ onNav }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const cols = [
    ["Range", [["Dish Wash", "shop"], ["Hand Wash", "shop"], ["Laundry Wash", "shop"], ["Refills", "shop"]]],
    ["Klaren", [["Philosophy", "philosophy"], ["Our promise", "promise"], ["The range", "shop"]]],
    ["Care", [["Shipping", "shop"], ["Returns", "shop"], ["Contact", "contact"]]],
  ];
  const subscribe = async () => {
    const e = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Please enter a valid email."); return; }
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/newsletter`, { email: e });
      toast.success(data.message || "Subscribed.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <footer id="contact" style={{ background: "var(--ink)", color: "var(--paper)", position: "relative" }}>
      <div style={{ ...WRAP, padding: "clamp(72px,11vh,130px) clamp(20px,4vw,56px) 40px" }}>
        <Reveal style={{ textAlign: "center", marginBottom: "clamp(54px,8vh,92px)" }}>
          <Display size="clamp(40px,8vw,116px)" style={{ lineHeight: 1.02, color: "var(--paper)", fontWeight: 400 }}>
            Live clean.<br />Live conscious.<br />Live <span className="k-serif-i">klaren.</span>
          </Display>
          <p style={{ color: "rgba(246,241,232,.6)", fontSize: 16, marginTop: 24, maxWidth: 460, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>Built for conscious human beings — home care that’s clear, gentle, effective, and honest.</p>
        </Reveal>

        <div className="k-foot-top" style={{ display: "flex", justifyContent: "space-between", gap: 48, flexWrap: "wrap", paddingBottom: 54, borderBottom: "1px solid rgba(246,241,232,.16)" }}>
          <div style={{ maxWidth: 360 }}>
            <span className="k-eye" style={{ color: "rgba(246,241,232,.55)" }}>Join the quiet list</span>
            <p style={{ color: "rgba(246,241,232,.72)", fontSize: 15, lineHeight: 1.6, margin: "14px 0 18px" }}>Slow emails. New essentials, refills, and the occasional note on conscious living.</p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid rgba(246,241,232,.3)", paddingBottom: 10 }}>
              <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") subscribe(); }} placeholder="you@home.com" data-testid="newsletter-input" style={{ flex: 1, background: "none", border: "none", outline: "none", color: "var(--paper)", fontFamily: "var(--font-sans)", fontSize: 15 }} />
              <button data-hover data-testid="newsletter-submit" onClick={subscribe} disabled={busy} style={{ background: "none", border: "none", color: "var(--paper)", cursor: busy ? "default" : "pointer", display: "flex", opacity: busy ? 0.5 : 1 }}><Icon name={busy ? "circle-notch" : "arrow-right"} size={20} style={{ animation: busy ? "k-spin 1s linear infinite" : "none" }} /></button>
            </div>
          </div>
          <div style={{ display: "flex", gap: "clamp(28px,5vw,76px)", flexWrap: "wrap" }}>
            {cols.map(([h, links]) => (
              <div key={h}>
                <div className="k-eye" style={{ color: "rgba(246,241,232,.5)", marginBottom: 16 }}>{h}</div>
                {links.map(([l, r]) => <div key={l} data-hover onClick={() => onNav(r)} style={{ fontSize: 14.5, color: "rgba(246,241,232,.78)", padding: "6px 0", cursor: "pointer" }}>{l}</div>)}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, paddingTop: 28 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 9 }}><Mark color="var(--paper)" size={20} /><Wordmark color="var(--paper)" size={20} /></span>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 12, color: "rgba(246,241,232,.5)", letterSpacing: ".04em" }}>© 2026 Klaren · Skin-kind home care</span>
        </div>
      </div>
    </footer>
  );
}
